import React, {Dispatch, useCallback, useEffect, useMemo, useState} from 'react'
import {formatDateToBrazilian, KTSVG} from '../../../helpers'
import {useMutation, useQuery, useQueryClient} from 'react-query'
import {useIntl} from 'react-intl'
import Swal from 'sweetalert2'
import {useAuth} from '../../../../app/modules/auth'
import {
  applyCoupon,
  getSubscriptionAmount,
  updateSubscription,
} from '../../../../app/modules/billings/components/billings-header/core/_requests'
import {getCreditCards} from '../../../../app/modules/billings/components/core/_requests'
import {Modal, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {LEGACY_UPGRADE_PLAN_OPTIONS, UPGRADE_PLAN_CONFIG} from './upgrade-plan.config'
import {
  PlanKey,
  PlanOption,
  SubscriptionAmountData,
  UpgradePlanKey,
  UpgradePlanOption,
} from './upgrade-plan.types'

const EMPTY_SUBSCRIPTION_AMOUNT = {} as SubscriptionAmountData

const UPGRADE_PLAN_KEYS: UpgradePlanKey[] = UPGRADE_PLAN_CONFIG.INCLUDE_LEGACY_PLANO_PRO
  ? ['PLANO_PRO', 'PRO', 'GROWTH']
  : ['PRO', 'GROWTH']

const getBackendNameForPlanKey = (planKey: PlanKey): string => {
  switch (planKey) {
    case 'PLANO_PRO':
      return 'Plano Pro'
    case 'PRO':
      return 'Pro'
    case 'GROWTH':
      return 'Growth'
    default:
      return 'Starter'
  }
}

const BASE_PLAN_OPTIONS: PlanOption[] = [
  {
    key: 'STARTER',
    title: 'Starter',
    backendName: 'Starter',
    subTitle: 'Para começar na plataforma.',
    description: 'Plano inicial para operação enxuta com os recursos essenciais.',
    custom: false,
    minUsers: 1,
    maxUsers: 1,
    features: [
      {title: 'Até 3 projetos ativos', supported: true},
      {title: 'Gestão de clientes', supported: true},
      {title: 'Time tracking', supported: true},
      {title: 'Relatórios de rentabilidade', supported: false},
      {title: 'Gestão de contratos', supported: false},
      {title: 'Portal do cliente', supported: false},
      {title: 'RushMind IA', supported: false},
      {title: 'Sprints e Kanban avançado', supported: false},
      {title: 'Suporte WhatsApp', supported: false},
    ],
  },
  {
    key: 'PRO',
    title: 'Pro',
    backendName: 'Pro',
    subTitle: 'Para profissionais e times pequenos.',
    description: 'Plano com recursos avançados para colaboração e produtividade.',
    label: 'Mais popular',
    custom: false,
    minUsers: UPGRADE_PLAN_CONFIG.MIN_USERS_NUMBER,
    maxUsers: 9,
    features: [
      {title: 'Projetos e tarefas ilimitadas', supported: true},
      {title: 'Gestão de clientes', supported: true},
      {title: 'Time tracking completo', supported: true},
      {title: 'Relatórios de rentabilidade ✦', supported: true},
      {title: 'Gestão de contratos', supported: true},
      {title: 'Portal do cliente (até 3)', supported: true},
      {title: 'RushMind IA ✦', supported: true},
      {title: 'Sprints, Kanban e Gantt', supported: true},
      {title: 'Wiki', supported: true},
      {title: 'Suporte WhatsApp e email', supported: true},
    ],
  },
  {
    key: 'GROWTH',
    title: 'Growth',
    backendName: 'Growth',
    subTitle: 'Para operações em crescimento.',
    description: 'Plano escalável com usuários inclusos e suporte prioritário.',
    minUsers: UPGRADE_PLAN_CONFIG.MIN_USERS_NUMBER,
    maxUsers: null,
    custom: false,
    features: [
      {title: 'Tudo do Pro', supported: true},
      {title: 'Até 10 usuários inclusos', supported: true},
      {title: 'Portal do cliente flexível ✦', supported: true},
      {title: 'Onboarding guiado (1 sessão)', supported: true},
      {title: 'Suporte prioritário', supported: true},
      {title: 'Acesso antecipado a novas funcionalidades', supported: true},
      {title: 'Usuário adicional: R$ 29/mês', supported: true},
    ],
  },
]

const PLAN_OPTIONS: PlanOption[] = UPGRADE_PLAN_CONFIG.INCLUDE_LEGACY_PLANO_PRO
  ? [
      BASE_PLAN_OPTIONS[0],
      ...LEGACY_UPGRADE_PLAN_OPTIONS,
      ...BASE_PLAN_OPTIONS.slice(1),
    ]
  : BASE_PLAN_OPTIONS

type Props = {
  show: boolean
  handleClose: () => void | undefined
  setOpenNewCreditCardModal: Dispatch<React.SetStateAction<boolean>>
  isOpenCreditCardModal: boolean
}

export function UpgradePlan({
  show,
  handleClose,
  setOpenNewCreditCardModal,
  isOpenCreditCardModal,
}: Props) {
  const restoreBodyScrollIfNeeded = () => {
    if (typeof document === 'undefined') return

    // Wait one tick so Bootstrap can finish modal teardown.
    window.setTimeout(() => {
      const hasOpenBootstrapModal = !!document.querySelector('.modal.show')
      const hasOpenSwalModal = !!document.querySelector('.swal2-container.swal2-backdrop-show')

      if (!hasOpenBootstrapModal && !hasOpenSwalModal) {
        document.body.classList.remove('modal-open')
        document.body.style.removeProperty('overflow')
        document.body.style.removeProperty('padding-right')
      }
    }, 0)
  }

  const handleCloseModal = () => {
    handleClose()
    restoreBodyScrollIfNeeded()
  }

  const getPlanKeyFromName = (name?: string): PlanKey => {
    const normalized = (name ?? '').trim().toLowerCase()
    if (normalized === 'growth') return 'GROWTH'
    if (normalized === 'plano pro') return 'PLANO_PRO'
    if (normalized === 'pro') return 'PRO'
    return 'STARTER'
  }

  const getPlanOption = (key: PlanKey) =>
    PLAN_OPTIONS.find((plan) => plan.key === key) ?? PLAN_OPTIONS[0]
  const getInitialUpgradePlan = (name?: string): UpgradePlanKey => {
    const key = getPlanKeyFromName(name)
    if (key === 'GROWTH') return 'GROWTH'
    if (key === 'PLANO_PRO' && UPGRADE_PLAN_CONFIG.INCLUDE_LEGACY_PLANO_PRO) return 'PLANO_PRO'
    return 'PRO'
  }

  const {currentUser} = useAuth()
  const subscriptionPlans = currentUser?.instance?.subscription_plans

  const currentPlanId =
    currentUser?.instance?.active_subscription?.plan_id ??
    currentUser?.instance?.plan_id ??
    null

  const getSubscriptionPlanRow = useCallback(
    (planKey: PlanKey) =>
      subscriptionPlans?.find(
        (p) =>
          p?.name &&
          p.name.toLowerCase() === getBackendNameForPlanKey(planKey).toLowerCase()
      ),
    [subscriptionPlans]
  )

  const isCurrentPlanKey = useCallback(
    (planKey: PlanKey): boolean => {
      const row = getSubscriptionPlanRow(planKey)
      return row != null && currentPlanId != null && row.id === currentPlanId
    },
    [getSubscriptionPlanRow, currentPlanId]
  )

  const billedUsersNumber =
    currentUser?.instance?.active_subscription?.total_users ??
    currentUser?.instance?.total_users ??
    0
  const hasActiveSubscription = !!currentUser?.instance?.active_subscription

  const getNewSubscriptionUserLimits = useCallback(
    (planKey: PlanKey): {minUsers: number; maxUsers: number | null} => {
      const base = getPlanOption(planKey)
      const row = getSubscriptionPlanRow(planKey)
      if (!row) {
        return {minUsers: base.minUsers, maxUsers: base.maxUsers}
      }

      const minUsers =
        row.effective_min_subscription_users ??
        row.included_users_number ??
        row.min_users_number ??
        UPGRADE_PLAN_CONFIG.MIN_ADDITIONAL_USERS

      let maxUsers: number | null
      if (row.max_users_number === undefined) {
        maxUsers = base.maxUsers
      } else if (row.max_users_number === null) {
        maxUsers = null
      } else if (Number.isFinite(Number(row.max_users_number))) {
        maxUsers = Math.floor(Number(row.max_users_number))
      } else {
        maxUsers = base.maxUsers
      }

      return {minUsers, maxUsers}
    },
    [getSubscriptionPlanRow]
  )

  const getAdditionalUserLimits = useCallback(
    (planKey: PlanKey): {minAdditional: number; maxAdditional: number | null} => {
      const {maxUsers} = getNewSubscriptionUserLimits(planKey)
      const minAdditional = UPGRADE_PLAN_CONFIG.MIN_ADDITIONAL_USERS
      if (maxUsers === null) {
        return {minAdditional, maxAdditional: null}
      }
      return {
        minAdditional,
        maxAdditional: Math.max(0, maxUsers - billedUsersNumber),
      }
    },
    [getNewSubscriptionUserLimits, billedUsersNumber]
  )

  const normalizeAdditionalUsersForPlan = useCallback(
    (value: number, planKey: PlanKey): number => {
      const {minAdditional, maxAdditional} = getAdditionalUserLimits(planKey)
      const safe = Number.isFinite(value) ? Math.floor(value) : minAdditional
      const minApplied = Math.max(minAdditional, safe)
      if (maxAdditional !== null) {
        return Math.min(maxAdditional, minApplied)
      }
      return minApplied
    },
    [getAdditionalUserLimits]
  )

  const normalizeNewSubscriptionUsersForPlan = useCallback(
    (value: number, planKey: PlanKey): number => {
      const {minUsers, maxUsers} = getNewSubscriptionUserLimits(planKey)
      const safe = Number.isFinite(value) ? Math.floor(value) : minUsers
      const minApplied = Math.max(minUsers, safe)
      if (maxUsers !== null) {
        return Math.min(maxUsers, minApplied)
      }
      return minApplied
    },
    [getNewSubscriptionUserLimits]
  )

  const getDefaultUsersInputForPlan = useCallback(
    (planKey: PlanKey): number => {
      if (isCurrentPlanKey(planKey)) {
        return UPGRADE_PLAN_CONFIG.DEFAULT_ADDITIONAL_USERS
      }
      return getNewSubscriptionUserLimits(planKey).minUsers
    },
    [isCurrentPlanKey, getNewSubscriptionUserLimits]
  )

  const normalizeUsersInputForPlan = useCallback(
    (value: number, planKey: PlanKey): number => {
      if (isCurrentPlanKey(planKey)) {
        return normalizeAdditionalUsersForPlan(value, planKey)
      }
      return normalizeNewSubscriptionUsersForPlan(value, planKey)
    },
    [isCurrentPlanKey, normalizeAdditionalUsersForPlan, normalizeNewSubscriptionUsersForPlan]
  )

  const getTargetTotalUsersForPlan = useCallback(
    (planKey: PlanKey, usersInput: number): number => {
      if (isCurrentPlanKey(planKey)) {
        const additional = normalizeAdditionalUsersForPlan(usersInput, planKey)
        if (hasActiveSubscription) {
          return billedUsersNumber + additional
        }
        return additional
      }
      return normalizeNewSubscriptionUsersForPlan(usersInput, planKey)
    },
    [
      isCurrentPlanKey,
      normalizeAdditionalUsersForPlan,
      normalizeNewSubscriptionUsersForPlan,
      hasActiveSubscription,
      billedUsersNumber,
    ]
  )

  const formatNewSubscriptionLimitsLabel = useCallback(
    (planKey: PlanKey): string => {
      const {minUsers, maxUsers} = getNewSubscriptionUserLimits(planKey)
      if (maxUsers !== null) {
        return `Nova assinatura. Mínimo de ${minUsers} e máximo de ${maxUsers} usuários.`
      }
      return `Nova assinatura. Mínimo de ${minUsers} usuários.`
    },
    [getNewSubscriptionUserLimits]
  )

  const [recurrenceType, setRecurrenceType] = useState<'MONTHLY' | 'YEARLY'>('MONTHLY')
  const [coupon, setCoupon] = useState('')
  const [couponId, setCouponId] = useState(currentUser?.instance?.active_subscription?.coupon_id)
  const [hasCoupon, setHasCoupon] = useState(false)
  const intl = useIntl()
  const queryClient = useQueryClient()
  const [selectedPlan, setSelectedPlan] = useState<UpgradePlanKey>(
    getInitialUpgradePlan(currentUser?.instance?.plan?.name)
  )
  const [usersInputNumber, setUsersInputNumber] = useState<number>(
    UPGRADE_PLAN_CONFIG.DEFAULT_ADDITIONAL_USERS
  )
  const [selectedCard, setSelectedCard] = useState<number>()
  const [isSubmiting, setIsSubmiting] = useState(false)
  const isSelectedPlanCurrent = isCurrentPlanKey(selectedPlan)
  const selectedUserLimits = useMemo(() => {
    if (isSelectedPlanCurrent) {
      const limits = getAdditionalUserLimits(selectedPlan)
      return {minUsers: limits.minAdditional, maxUsers: limits.maxAdditional}
    }
    return getNewSubscriptionUserLimits(selectedPlan)
  }, [isSelectedPlanCurrent, selectedPlan, getAdditionalUserLimits, getNewSubscriptionUserLimits])
  const selectedPlanOption = useMemo(() => {
    const base = getPlanOption(selectedPlan)
    return {...base, ...selectedUserLimits}
  }, [selectedPlan, selectedUserLimits])
  const plans = useMemo<UpgradePlanOption[]>(() => {
    return PLAN_OPTIONS.filter((plan): plan is UpgradePlanOption => {
      if (!UPGRADE_PLAN_KEYS.includes(plan.key as UpgradePlanKey)) return false
      // Plano Pro legado: só para quem já está nele (adicionar usuários ou trocar a periodicidade).
      // O signup também recusa a troca para ele vinda de outro plano.
      if (
        plan.key === 'PLANO_PRO' &&
        getPlanKeyFromName(currentUser?.instance?.plan?.name) !== 'PLANO_PRO'
      )
        return false
      if (!subscriptionPlans?.length) {
        return plan.key === 'PRO' || plan.key === 'GROWTH'
      }
      return subscriptionPlans.some(
        (row) =>
          row?.name &&
          row.name.toLowerCase() === getBackendNameForPlanKey(plan.key).toLowerCase()
      )
    })
  }, [subscriptionPlans, currentUser?.instance?.plan?.name])
  const normalizedUsersInput = useMemo(
    () => normalizeUsersInputForPlan(usersInputNumber, selectedPlan),
    [usersInputNumber, selectedPlan, normalizeUsersInputForPlan]
  )
  const normalizedAdditionalUsers = useMemo(
    () =>
      isSelectedPlanCurrent
        ? normalizedUsersInput
        : 0,
    [isSelectedPlanCurrent, normalizedUsersInput]
  )
  const targetTotalUsers = useMemo(
    () => getTargetTotalUsersForPlan(selectedPlan, usersInputNumber),
    [selectedPlan, usersInputNumber, getTargetTotalUsersForPlan]
  )
  const currentPlanKey = getPlanKeyFromName(currentUser?.instance?.plan?.name)
  const hasChange = isSelectedPlanCurrent
    ? currentUser?.instance?.active_subscription?.recurrence_type != recurrenceType ||
      normalizedAdditionalUsers > 0
    : currentUser?.instance?.active_subscription?.recurrence_type != recurrenceType ||
      currentPlanKey !== selectedPlan

  const {data: cards = []} = useQuery(`credit-cards`, () => getCreditCards(), {
    cacheTime: 0,
    onError: (err) => console.error(err),
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (currentUser?.instance?.active_subscription?.recurrence_type) {
      setRecurrenceType(
        currentUser?.instance?.active_subscription?.recurrence_type as 'MONTHLY' | 'YEARLY'
      )
    }
    if (currentUser?.instance?.plan?.name) {
      const key = getInitialUpgradePlan(currentUser.instance.plan.name)
      setSelectedPlan(key)
      setUsersInputNumber(getDefaultUsersInputForPlan(key))
    }
  }, [currentUser, getDefaultUsersInputForPlan])

  useEffect(() => {
    setUsersInputNumber(getDefaultUsersInputForPlan(selectedPlan))
  }, [selectedPlan, getDefaultUsersInputForPlan])

  useEffect(() => {
    setUsersInputNumber((prev) => normalizeUsersInputForPlan(prev, selectedPlan))
  }, [subscriptionPlans, normalizeUsersInputForPlan, selectedPlan])

  const resolveUsersInputForPlanQuery = useCallback(
    (planKey: UpgradePlanKey): number =>
      selectedPlan === planKey ? usersInputNumber : getDefaultUsersInputForPlan(planKey),
    [selectedPlan, usersInputNumber, getDefaultUsersInputForPlan]
  )

  const shouldFetchPlanoProAmount = useMemo(
    () =>
      UPGRADE_PLAN_CONFIG.INCLUDE_LEGACY_PLANO_PRO &&
      (subscriptionPlans?.some(
        (row) => row?.name && row.name.toLowerCase() === 'plano pro'
      ) ??
        false),
    [subscriptionPlans]
  )

  const {data: subscriptionAmountWithPlanoProPlan = EMPTY_SUBSCRIPTION_AMOUNT} = useQuery(
    [
      usersInputNumber,
      billedUsersNumber,
      currentPlanId,
      recurrenceType,
      couponId,
      'plano-pro',
    ],
    async () =>
      getSubscriptionAmount({
        planName: 'Plano Pro',
        total_users: getTargetTotalUsersForPlan(
          'PLANO_PRO',
          resolveUsersInputForPlanQuery('PLANO_PRO')
        ),
        cycle: recurrenceType,
        couponId,
      }),
    {
      enabled: shouldFetchPlanoProAmount,
      cacheTime: 0,
      onError: (err) => console.error(err),
      refetchOnWindowFocus: false,
    }
  )

  const {data: subscriptionAmountWithProPlan = EMPTY_SUBSCRIPTION_AMOUNT} = useQuery(
    [usersInputNumber, billedUsersNumber, currentPlanId, recurrenceType, couponId, 'pro'],
    async () =>
      getSubscriptionAmount({
        planName: 'Pro',
        total_users: getTargetTotalUsersForPlan('PRO', resolveUsersInputForPlanQuery('PRO')),
        cycle: recurrenceType,
        couponId,
      }),
    {
      cacheTime: 0,
      onError: (err) => console.error(err),
      refetchOnWindowFocus: false,
    }
  )

  const {data: subscriptionAmountWithGrowthPlan = EMPTY_SUBSCRIPTION_AMOUNT} = useQuery(
    [usersInputNumber, billedUsersNumber, currentPlanId, recurrenceType, couponId, 'growth'],
    async () =>
      getSubscriptionAmount({
        planName: 'Growth',
        total_users: getTargetTotalUsersForPlan('GROWTH', resolveUsersInputForPlanQuery('GROWTH')),
        cycle: recurrenceType,
        couponId,
      }),
    {
      cacheTime: 0,
      onError: (err) => console.error(err),
      refetchOnWindowFocus: false,
    }
  )

  const getPlanAmount = useCallback(
    (planKey: UpgradePlanKey): SubscriptionAmountData => {
      switch (planKey) {
        case 'PLANO_PRO':
          return subscriptionAmountWithPlanoProPlan
        case 'PRO':
          return subscriptionAmountWithProPlan
        case 'GROWTH':
          return subscriptionAmountWithGrowthPlan
      }
    },
    [
      subscriptionAmountWithPlanoProPlan,
      subscriptionAmountWithProPlan,
      subscriptionAmountWithGrowthPlan,
    ]
  )

  const annualPlanDiscountLabel = useMemo(() => {
    const rows = currentUser?.instance?.subscription_plans
    if (!rows?.length) return null
    const name = selectedPlanOption.backendName
    const row = rows.find(
      (p) => p.name && name && p.name.toLowerCase() === name.toLowerCase()
    )
    if (!row || row.annual_discount_percent == null) return null
    const n = Number(row.annual_discount_percent)
    if (!Number.isFinite(n) || n <= 0) return null
    return {percent: n, planName: row.name}
  }, [currentUser?.instance?.subscription_plans, selectedPlanOption])

  const {mutateAsync: updateFn} = useMutation(
    () =>
      updateSubscription({
        instanceId: Number(currentUser?.instance?.id),
        planName: selectedPlanOption.backendName,
        usersNumber: targetTotalUsers,
        recurrenceType,
        value: getPlanAmount(selectedPlan).newValue,
        cardId: selectedCard!,
        coupon: String(couponId),
      }),
    {
      onSuccess: (data, variable) => {
        Swal.fire({
          icon: 'success',
          title: 'Assinatura atualizada.',
        })
        queryClient.invalidateQueries()
      },
      onError: (error: any) => {
        Swal.fire({
          icon: 'warning',
          html: error.response.data.message,
        })
      },
    }
  )

  function getCouponDescription(response: {
    numberOfInvoices: number | null | undefined
    recurrenceType: string
  }): string {
    if (response.numberOfInvoices === null || response.numberOfInvoices === undefined) {
      return ''
    }

    if (response.numberOfInvoices === 0) {
      return 'Cupom expirado'
    }

    if (response.numberOfInvoices === 1) {
      return 'Esse valor promocional será apenas na primeira fatura.'
    }

    const recurrenceTypeLower = response.recurrenceType?.toLowerCase() || ''
    const invoiceText =
      recurrenceTypeLower === 'yearly' ||
      recurrenceTypeLower === 'annual' ||
      response.recurrenceType === 'YEARLY'
        ? 'faturas anuais'
        : 'faturas'
    return `Esse valor promocional será para as próximas ${response.numberOfInvoices} ${invoiceText}.`
  }

  const {mutateAsync: applyCouponFn} = useMutation(
    () =>
      applyCoupon({
        planName: selectedPlanOption.backendName,
        total_users: targetTotalUsers,
        cycle: recurrenceType,
        coupon,
      }),
    {
      onSuccess: (data, variable) => {
        setCouponId(data.data.id)
        queryClient.invalidateQueries()
      },
      onError: (error: any) => {
        Swal.fire({
          icon: 'warning',
          text: error.response.data.error,
        })
      },
    }
  )

  function calculateCouponEndDate(numberOfInvoices: number | null, recurrenceType: string): string {
    if (!numberOfInvoices || numberOfInvoices === 0) {
      return ''
    }

    const today = new Date()
    let endDate = new Date(today)

    if (recurrenceType === 'YEARLY' || recurrenceType === 'annual') {
      endDate.setFullYear(today.getFullYear() + numberOfInvoices)
    } else {
      endDate.setMonth(today.getMonth() + numberOfInvoices)
    }

    return formatDateToBrazilian(endDate.toISOString())
  }

  function confirmUpgrade() {
    setIsSubmiting(true)
    const TemaAtual = window.localStorage.getItem('kt_theme_mode_value') || ''
    const tema = TemaAtual === 'dark' ? '#353b48' : '#fff'

    const selectedPlanData = getPlanAmount(selectedPlan)
    const isYearlyToMonthlyChange =
      currentUser?.instance?.active_subscription?.recurrence_type === 'YEARLY' &&
      recurrenceType === 'MONTHLY'
    const selectedPlanPartialValue = getPartialChargeValue(selectedPlan)
    const shouldShowSelectedPlanPartialCharge =
      !isYearlyToMonthlyChange &&
      !!currentUser?.instance?.active_subscription &&
      normalizedAdditionalUsers > 0 &&
      isSelectedPlanCurrent &&
      selectedPlanPartialValue > 0

    const hasCoupon =
      selectedPlanData.originalValue &&
      selectedPlanData.discountPercentage &&
      selectedPlanData.numberOfInvoices !== null &&
      selectedPlanData.numberOfInvoices !== undefined &&
      selectedPlanData.numberOfInvoices > 0

    const couponEndDate = hasCoupon
      ? calculateCouponEndDate(
          selectedPlanData.numberOfInvoices!,
          selectedPlanData.recurrenceType || recurrenceType
        )
      : ''

    const additionalUsersSummary = isSelectedPlanCurrent
      ? normalizedAdditionalUsers > 0
        ? `<p>Adicionando <b>${normalizedAdditionalUsers}</b> usuário(s) avulso(s). Total no plano: <b>${targetTotalUsers}</b>.</p>`
        : ''
      : `<p>Nova assinatura com <b>${targetTotalUsers}</b> usuário(s). ${formatNewSubscriptionLimitsLabel(selectedPlan).replace('Nova assinatura. ', '')}</p>`

    Swal.fire({
      title: 'Confirmação de Upgrade',

      html:
        currentUser?.instance?.active_subscription?.recurrence_type == 'YEARLY' &&
        recurrenceType == 'MONTHLY'
          ? `
                ${additionalUsersSummary}
                <p class="mb-0"><b>Novo valor da assinatura: ${Number(
                  getPlanAmount(selectedPlan).newValue
                ).toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                })} /${recurrenceType == 'MONTHLY' ? 'mês' : 'ano'}
                </b></p>
                <p>Esse valor será cobrado somente após o vencimento do plano anual atual (${formatDateToBrazilian(
                  currentUser?.instance?.active_subscription?.expiry_date
                )}).</p>
            ${
              shouldShowSelectedPlanPartialCharge
                ? `<p><b>Valor parcial a ser cobrado agora: ${Number(
                    selectedPlanPartialValue
                  ).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
            </b></p>`
                : ''
            }
            ${
              hasCoupon
                ? `
            <p><b>Valor após ${couponEndDate}:</b> ${Number(
                    selectedPlanData.originalValue
                  ).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })} /${recurrenceType == 'MONTHLY' ? 'mês' : 'ano'}</p>
            `
                : ''
            }

            <p>A atualização será aplicada imediatamente após a confirmação.</p>

            <p>Deseja confirmar o upgrade?</p>`
          : `
            ${additionalUsersSummary}
            <p>Novo valor da assinatura: ${Number(
              getPlanAmount(selectedPlan).newValue
            ).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })} /${recurrenceType == 'MONTHLY' ? 'mês' : 'ano'}
            ${
              shouldShowSelectedPlanPartialCharge
                ? `<br/> Pagamento parcial a ser cobrado agora: ${Number(
                    selectedPlanPartialValue
                  ).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
            </p>`
                : '</p>'
            }
            ${
              hasCoupon
                ? `
            <p><b>Valor após ${couponEndDate}:</b> ${Number(
                    selectedPlanData.originalValue
                  ).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })} /${recurrenceType == 'MONTHLY' ? 'mês' : 'ano'}</p>
            `
                : ''
            }

            <p>A atualização será aplicada imediatamente após a confirmação.</p>

            <p>Deseja confirmar o upgrade?</p>`,
      showCloseButton: true,
      background: tema,
      confirmButtonColor: '#009ef7',
      confirmButtonText: 'Confirmar',
    })
      .then(async (result) => {
        if (result.isConfirmed) await updateFn().then(() => handleCloseModal())
      })
      .finally(() => setIsSubmiting(false))
  }

  function getPartialChargeValue(planKey: UpgradePlanKey): number {
    const partialCharge = getPlanAmount(planKey).partialCharge
    if (partialCharge == null || !Number.isFinite(Number(partialCharge))) {
      return 0
    }

    return Math.max(Number(partialCharge), 0)
  }

  return (
    <Modal
      id='kt_modal_upgrade_plan'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName={`modal-dialog modal-dialog-centered`}
      show={show}
      backdrop='static'
      onHide={handleCloseModal}
      onExited={restoreBodyScrollIfNeeded}
      size='xl'
      style={{zIndex: isOpenCreditCardModal ? 1000 : 1050}}
    >
      <div className='modal-content'>
        <div className='modal-header justify-content-end border-0 pb-0'>
          <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleCloseModal}>
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </div>
        </div>

        <div className='modal-body pt-0 pb-15 px-5 px-xl-20'>
          <div className='mb-13 text-center'>
            <h1 className='mb-3 text-center'>Atualize seu plano</h1>
          </div>
          <p className='fs-2'>
            Seu plano atual é o <b>{currentUser?.instance?.plan?.name}</b> com{' '}
            <b>{billedUsersNumber}</b>{' '}
            {billedUsersNumber === 1 ? 'usuário contratado' : 'usuários contratados'}
          </p>

          <div className='d-flex flex-column'>
            <div className='d-flex flex-column flex-lg-row align-items-center justify-content-center gap-8 mx-auto'>
              <div className='d-flex flex-column align-items-center'>
                {isSelectedPlanCurrent ? (
                  <>
                    <p className='text-center mb-1'>Quantos usuários avulsos adicionar?</p>
                    {hasActiveSubscription && (
                      <p className='text-muted small text-center mb-2'>
                        Total no plano: {billedUsersNumber} + {normalizedAdditionalUsers} ={' '}
                        {targetTotalUsers}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className='text-center mb-1'>Quantos usuários?</p>
                    <p className='text-muted small text-center mb-2'>
                      {formatNewSubscriptionLimitsLabel(selectedPlan)}
                    </p>
                  </>
                )}
                <div className='input-group flex-nowrap align-items-center' style={{width: 132}}>
                  <div className='input-group-prepend'>
                    <button
                      className='btn btn-outline-secondary border border-gray border-right-0 py-3 rounded-0'
                      type='button'
                      onClick={() =>
                        setUsersInputNumber((prev) =>
                          normalizeUsersInputForPlan(prev - 1, selectedPlan)
                        )
                      }
                      disabled={usersInputNumber <= selectedPlanOption.minUsers}
                    >
                      -
                    </button>
                  </div>
                  <input
                    type='number'
                    className='form-control text-center border-gray border border-1 shadow-none border-left-0 border-right-0 py-3'
                    name='usersInputNumber'
                    id='usersInputNumber'
                    value={usersInputNumber}
                    min={selectedPlanOption.minUsers}
                    max={selectedPlanOption.maxUsers ?? undefined}
                    autoComplete='off'
                    style={{width: 44, minWidth: 44, maxWidth: 44}}
                    onChange={(event) =>
                      setUsersInputNumber(
                        normalizeUsersInputForPlan(Number(event.target.value), selectedPlan)
                      )
                    }
                  />
                  <div className='input-group-append'>
                    <button
                      className='btn btn-outline-secondary border border-gray border-left-0 py-3 rounded-0'
                      type='button'
                      onClick={() =>
                        setUsersInputNumber((prev) =>
                          normalizeUsersInputForPlan(prev + 1, selectedPlan)
                        )
                      }
                      disabled={
                        selectedPlanOption.maxUsers !== null &&
                        usersInputNumber >= selectedPlanOption.maxUsers
                      }
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
              <div className='d-flex flex-column mt-4 mt-lg-0 align-items-center'>
                {/* Altura mínima fixa: sem desconto o layout não "pula" em relação ao bloco "Quantos usuários?" */}
                <div
                  className='w-100 text-center d-flex flex-column justify-content-center'
                  style={{minHeight: '2.75rem'}}
                >
                  {annualPlanDiscountLabel != null && (
                    <p className='text-center text-primary mb-0'>
                      <b>
                        {new Intl.NumberFormat('pt-BR', {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 0,
                        }).format(annualPlanDiscountLabel.percent)}
                        % de desconto no plano anual ({annualPlanDiscountLabel.planName})
                      </b>
                    </p>
                  )}
                </div>
                <div
                  className='nav-group nav-group-outline p-0 my-0 mx-auto d-flex flex-nowrap align-items-stretch gap-0'
                  role='group'
                  aria-label='Cobrança mensal ou anual'
                  data-kt-buttons='true'
                >
                  <a
                    href='#'
                    className={
                      'btn m-0 btn-color-gray-400 btn-active btn-active-secondary border-0 shadow-none px-6 py-3 rounded-0 text-nowrap d-flex align-items-center justify-content-center ' +
                      (recurrenceType === 'MONTHLY' && 'active')
                    }
                    onClick={() => {
                      setRecurrenceType('MONTHLY')
                    }}
                    data-kt-plan='month'
                  >
                    Mensal
                  </a>
                  <a
                    href='#'
                    className={
                      'btn m-0 btn-color-gray-400 btn-active btn-active-secondary border-0 shadow-none px-6 py-3 rounded-0 text-nowrap d-flex align-items-center justify-content-center ' +
                      (recurrenceType === 'YEARLY' && 'active')
                    }
                    onClick={() => {
                      setRecurrenceType('YEARLY')
                    }}
                    data-kt-plan='annual'
                  >
                    Anual
                  </a>
                </div>
              </div>
            </div>

            <div className='row mt-10'>
              <div className='col-lg-6 mb-10 mb-lg-0'>
                <div className='nav flex-column'>
                  {plans.map((plan, index) => {
                    const planAmount = getPlanAmount(plan.key)
                    const originalValue = planAmount.originalValue
                    const newValue = planAmount.newValue
                    const partialValueForPlan = getPartialChargeValue(plan.key)
                    const isPlanCurrent = isCurrentPlanKey(plan.key)
                    const isAddingUsers = isPlanCurrent && normalizedUsersInput > 0 && selectedPlan === plan.key
                    const shouldShowPartialChargeForPlan =
                      selectedPlan === plan.key &&
                      !(currentUser?.instance?.active_subscription?.recurrence_type === 'YEARLY' &&
                        recurrenceType === 'MONTHLY') &&
                      !!currentUser?.instance?.active_subscription &&
                      isAddingUsers &&
                      partialValueForPlan > 0
                    const showRenewalPriceLabel =
                      selectedPlan === plan.key &&
                      !!currentUser?.instance?.active_subscription &&
                      !shouldShowPartialChargeForPlan
                    const planTargetTotalUsers = getTargetTotalUsersForPlan(
                      plan.key,
                      selectedPlan === plan.key ? usersInputNumber : getDefaultUsersInputForPlan(plan.key)
                    )

                    const hasDifferentOriginal =
                      typeof originalValue === 'number' &&
                      typeof newValue === 'number' &&
                      originalValue !== newValue

                    return (
                      <div
                        onClick={() => {
                          setSelectedPlan(plan.key)
                          setUsersInputNumber(getDefaultUsersInputForPlan(plan.key))
                        }}
                        className={
                          `nav-link btn btn-outline btn-outline-dashed btn-color-dark d-flex flex-stack text-start p-6 mb-6 ${
                            plan.key === selectedPlan && 'active'
                          } ` + (!plan.custom && 'btn-active btn-active-primary ')
                        }
                        data-bs-toggle='tab'
                        data-bs-target={`#kt_upgrade_plan_${index}`}
                        key={plan.title}
                      >
                        <div className='d-flex align-items-center me-2'>
                          <div className='form-check form-check-custom form-check-solid form-check-success me-6'>
                            <input
                              className='form-check-input'
                              type='radio'
                              name='plan'
                              value={plan.key}
                              checked={selectedPlan === plan.key}
                              onChange={() => {
                                setSelectedPlan(plan.key)
                                setUsersInputNumber(getDefaultUsersInputForPlan(plan.key))
                              }}
                            />
                          </div>

                          <div className='flex-grow-1'>
                            <h2 className='d-flex align-items-center fs-2 fw-bolder flex-wrap'>
                              {plan.title}

                              {plan.label && (
                                <span className='badge badge-light-success ms-2 fs-7'>
                                  {plan.label}
                                </span>
                              )}
                            </h2>
                            <div className='fw-bold opacity-50'>{plan.subTitle}</div>
                          </div>
                        </div>

                        <div className=''>
                          {plan.custom && (
                            <button className='btn btn-sm btn-primary'>Contact Us</button>
                          )}

                          {!plan.custom && (
                            <>
                              {shouldShowPartialChargeForPlan &&
                              !!currentUser?.instance?.active_subscription?.expiry_date ? (
                                <>
                                  <div className='d-flex align-items-start justify-content-end flex-column w-100'>
                                    <div className='d-flex align-items-center justify-content-end w-100'>
                                      {hasDifferentOriginal && (
                                        <span className='fs-6 fw-bolder text-muted text-decoration-line-through me-2'>
                                          {originalValue.toLocaleString('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                          })}
                                        </span>
                                      )}
                                      <span className='fs-6 fw-bolder'>
                                        {newValue?.toLocaleString('pt-BR', {
                                          style: 'currency',
                                          currency: 'BRL',
                                        })}
                                      </span>
                                      <span className='fs-7 opacity-50 ms-1'>
                                        /
                                        <span data-kt-element='period'>
                                          {recurrenceType == 'MONTHLY' ? 'mês' : 'ano'}
                                        </span>
                                      </span>
                                    </div>
                                    <div className='d-flex flex-column mt-2 w-100 align-items-end'>
                                      <span className='text-end w-100'>Você paga agora: </span>
                                      <OverlayTrigger
                                        placement='top'
                                        delay={{show: 50, hide: 100}}
                                        overlay={
                                          <Tooltip>
                                            Valor proporcional da diferenca entre seu plano atual e
                                            o novo plano, de hoje ate o fechamento da fatura atual.
                                          </Tooltip>
                                        }
                                      >
                                        <div className='d-flex align-items-center justify-content-end'>
                                          <span className='mt-2'>R$</span>
                                          <span className='fs-3x'>
                                            <strong>
                                              {Number(
                                                partialValueForPlan
                                              ).toLocaleString('pt-BR', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              })}
                                            </strong>
                                          </span>
                                        </div>
                                      </OverlayTrigger>
                                    </div>
                                    <div className='d-flex flex-wrap align-items-center justify-content-end mt-2 w-100'>
                                      {hasDifferentOriginal && planAmount.discountPercentage && (
                                          <span className='badge badge-light-success me-2'>
                                            Cupom de {planAmount.discountPercentage}% aplicado
                                          </span>
                                        )}
                                    </div>
                                    {hasDifferentOriginal &&
                                      planAmount.numberOfInvoices !== null &&
                                      planAmount.numberOfInvoices !== undefined && (
                                        <div
                                          className={`small mt-1 text-end w-100 ${
                                            selectedPlan === plan.key ? 'text-white' : 'text-muted'
                                          }`}
                                        >
                                          {getCouponDescription({
                                            numberOfInvoices: planAmount.numberOfInvoices,
                                            recurrenceType: planAmount.recurrenceType,
                                          })}
                                        </div>
                                      )}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className='d-flex align-items-start justify-content-end flex-column w-100'>
                                      {hasDifferentOriginal && (
                                      <div className='d-flex align-items-center justify-content-end w-100 mb-1'>
                                        <span className='fs-2 fw-bolder text-muted text-decoration-line-through'>
                                          {originalValue.toLocaleString('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                          })}
                                        </span>
                                      </div>
                                    )}
                                    <div className='d-flex align-items-center justify-content-end w-100'>
                                      <span className='mb-2'>R$</span>
                                      <span className='fs-3x fw-bolder'>
                                        {newValue?.toLocaleString('pt-BR', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </span>
                                      <span className='fs-7 opacity-50 ms-1'>
                                        /
                                        <span data-kt-element='period'>
                                          {recurrenceType == 'MONTHLY' ? 'mês' : 'ano'}
                                        </span>
                                      </span>
                                    </div>
                                    {showRenewalPriceLabel && (
                                      <div
                                        className={`small mt-1 text-end w-100 ${
                                          selectedPlan === plan.key ? 'text-white-50' : 'text-muted'
                                        }`}
                                      >
                                        {isPlanCurrent
                                          ? `Valor na próxima renovação (${planTargetTotalUsers} ${
                                              planTargetTotalUsers === 1 ? 'usuário' : 'usuários'
                                            }).`
                                          : `Nova assinatura (${planTargetTotalUsers} ${
                                              planTargetTotalUsers === 1 ? 'usuário' : 'usuários'
                                            }).`}
                                      </div>
                                    )}
                                    <div className='d-flex flex-wrap align-items-center justify-content-end mt-2 w-100'>
                                      {hasDifferentOriginal && planAmount.discountPercentage && (
                                          <span className='badge badge-light-success me-2'>
                                            Cupom de {planAmount.discountPercentage}% aplicado
                                          </span>
                                        )}
                                    </div>
                                    {hasDifferentOriginal &&
                                      planAmount.numberOfInvoices !== null &&
                                      planAmount.numberOfInvoices !== undefined && (
                                        <div
                                          className={`small mt-1 text-end w-100 ${
                                            selectedPlan === plan.key ? 'text-white' : 'text-muted'
                                          }`}
                                        >
                                          {getCouponDescription({
                                            numberOfInvoices: planAmount.numberOfInvoices,
                                            recurrenceType: planAmount.recurrenceType,
                                          })}
                                        </div>
                                      )}
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )
                  })}

                  {!hasCoupon ? (
                    <div className='col-4 d-flex flex-column fv-row mb-0'>
                      <button
                        className='btn btn-sm btn-secondary'
                        onClick={() => setHasCoupon(true)}
                      >
                        Possuo um cupom
                      </button>
                    </div>
                  ) : (
                    <div className='row'>
                      <div className='col-7 col-xl-8 d-flex flex-column fv-row'>
                        <input
                          type='text'
                          className='form-control form-control-sm form-control-solid input-cep'
                          name='coupon'
                          id='coupon'
                          value={coupon}
                          onChange={(e) => setCoupon(e.target.value)}
                        />
                      </div>
                      <div className='col-5 col-xl-4 d-flex flex-column fv-row'>
                        <button className='btn btn-sm btn-primary' onClick={() => applyCouponFn()}>
                          Aplicar cupom
                        </button>
                      </div>
                    </div>
                  )}

                  <div className='mt-8'>
                    <div className='d-flex justify-content-between align-items-center mb-4'>
                      <h3 className='fs-2'>Formas de pagamento</h3>
                      <button
                        className='btn btn-sm btn-primary'
                        onClick={() => setOpenNewCreditCardModal(true)}
                      >
                        Novo Cartão
                      </button>
                    </div>

                    {/* Container com rolagem para os cartões */}
                    <div
                      style={{maxHeight: '400px', overflowY: 'auto'}}
                      className='d-flex flex-column gap-4'
                    >
                      {cards.length > 0 ? (
                        cards.map((card) => (
                          <div
                            onClick={() => setSelectedCard(card.id)}
                            className={`nav-link btn btn-outline btn-color-dark d-flex flex-stack text-start px-6 py-4 ${
                              card.id == selectedCard && 'active'
                            } `}
                            key={card.id}
                          >
                            <div className='d-flex align-items-center me-2'>
                              <div className='form-check form-check-custom form-check-solid form-check-success me-6'>
                                <input
                                  className='form-check-input'
                                  type='radio'
                                  name='card'
                                  value={card.id}
                                  checked={selectedCard === card.id}
                                  onChange={(e) => setSelectedCard(Number(e.target.value))}
                                  role='button'
                                />
                              </div>

                              <div className='flex-grow-1'>
                                <div className='d-flex gap-4 align-items-center'>
                                  <div className='symbol'>
                                    <img alt='card' src='/media/svg/card-logos/mastercard.svg' />
                                  </div>

                                  <div>
                                    <div className='d-flex align-items-center gap-3'>
                                      <span className='fs-4 text-gray-800 fw-bolder mb-0'>
                                        Cartão{' '}
                                        {card.credit_card_brand.charAt(0).toUpperCase() +
                                          card.credit_card_brand.slice(1).toLowerCase()}
                                      </span>
                                    </div>
                                    <p className='fw-bold text-gray-400 mb-0'>
                                      Final {card.credit_card_last4}{' '}
                                      {`- Válido até ${card.credit_card_expiry_date}`}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className='text-center mt-4'>
                          Você ainda não possui um cartão cadastrado. Adicione um novo cartão para
                          habilitar o pagamento e concluir sua assinatura.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className='col-lg-6'>
                <div className='tab-content rounded h-100 bg-light p-10'>
                  {plans.map((plan, index) => {
                    return (
                      <div key={`custom${index}`}>
                        {!plan.custom && selectedPlan == plan.key && (
                          <div
                            className={`tab-pane fade show active`}
                            id={`kt_upgrade_plan_${index}`}
                            key={index}
                          >
                            <div className='pb-5'>
                              <h2 className='fw-bolder text-dark'>
                                O que está incluído no Plano {plan.title}?
                              </h2>

                              <div className='text-gray-400 fw-bold'>{plan.description}</div>
                            </div>

                            <div className='pt-1'>
                              {plan.features!.map((feature, i) => {
                                return (
                                  <div
                                    className={
                                      `d-flex align-items-center` +
                                      (i !== plan.features!.length - 1 && ' mb-7')
                                    }
                                    key={`${i}-${feature.title}`}
                                  >
                                    {feature.supported && (
                                      <>
                                        <span className='fw-bold fs-5 text-gray-700 flex-grow-1'>
                                          {feature.title}
                                        </span>

                                        <KTSVG
                                          path='/media/icons/duotune/general/gen043.svg'
                                          className='svg-icon-1 svg-icon-success'
                                        />
                                      </>
                                    )}
                                    {!feature.supported && (
                                      <>
                                        <span className='fw-bold fs-5 text-gray-400 flex-grow-1'>
                                          {feature.title}
                                        </span>
                                        <KTSVG
                                          path='/media/icons/duotune/general/gen040.svg'
                                          className='svg-icon-1'
                                        />
                                      </>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className='d-flex flex-center flex-row-fluid pt-12'>
            <button type='reset' className='btn btn-light me-3' onClick={handleCloseModal}>
              Cancelar
            </button>

            {!selectedCard || !hasChange ? (
              <OverlayTrigger
                placement='top'
                delay={{show: 50, hide: 100}}
                overlay={
                  <Tooltip>
                    {!hasChange
                      ? 'Nenhuma alteração foi feita.'
                      : 'Selecione um cartão para continuar.'}
                  </Tooltip>
                }
              >
                <span>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    onClick={() => confirmUpgrade()}
                    disabled
                  >
                    Atualizar plano
                  </button>
                </span>
              </OverlayTrigger>
            ) : (
              <button
                type='submit'
                className='btn btn-primary'
                onClick={() => confirmUpgrade()}
                disabled={!selectedCard || isSubmiting}
              >
                {!isSubmiting ? (
                  'Atualizar plano'
                ) : (
                  <span className=''>
                    {intl.formatMessage({id: 'GENERAL.LOADING'})}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}
